import { useState, useEffect } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, MessageSquareText, BellRing } from "lucide-react";
import ShippingSettingsCard from "./ShippingSettingsCard";

export default function SettingsPanel() {
  const { user } = useAuth();
  const canPortal = !!user && (user.is_admin || (user.permissions || []).includes("quoting"));
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [alert, setAlert] = useState({ enabled: true, recipients: [], staff: [] });
  const [alertBusy, setAlertBusy] = useState(false);

  useEffect(() => {
    if (!canPortal) return;
    api.get("/admin/portal-settings").then((r) => setMsg(r.data.message || "")).catch(() => {});
  }, [canPortal]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/alert-settings").then((r) => setAlert({
      enabled: r.data.enabled, recipients: r.data.recipients || [], staff: r.data.staff || [],
    })).catch(() => {});
  }, [user]);

  const saveAlerts = async (nextEnabled, nextRecipients) => {
    setAlertBusy(true);
    try {
      const r = await api.put("/admin/alert-settings", { enabled: nextEnabled, recipients: nextRecipients });
      setAlert((a) => ({ ...a, enabled: r.data.enabled, recipients: r.data.recipients }));
      toast.success("Alert settings saved");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setAlertBusy(false); }
  };

  const toggleRecipient = (email) => {
    const set = new Set(alert.recipients);
    set.has(email) ? set.delete(email) : set.add(email);
    setAlert((a) => ({ ...a, recipients: [...set] }));
  };

  const sendTestAlert = async () => {
    setAlertBusy(true);
    try {
      const r = await api.post("/admin/alert-settings/test");
      toast.success(`Test alert sent to ${r.data.sent_to}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setAlertBusy(false); }
  };

  const saveMessage = async () => {
    setMsgBusy(true);
    try {
      const r = await api.put("/admin/portal-settings", { message: msg });
      setMsg(r.data.message);
      toast.success("Portal message saved");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setMsgBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl">
      <header className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account.</p>
      </header>

      {user?.must_change_password && (
        <div className="mb-6 rounded-sm border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="change-password-banner">
          For your security, please set a new password below to replace the temporary one you were given.
        </div>
      )}

      <Card className="rounded-sm p-6 mb-6">
        <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Signed in as</div>
        <div className="font-medium text-slate-900 mt-1">{user?.email}</div>
      </Card>

      {canPortal && (
      <Card className="rounded-sm p-6 mb-6">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
          <MessageSquareText className="h-4 w-4" /> Portal welcome message
        </h2>
        <p className="text-sm text-slate-500 mb-4">Shown to customers on the sign-in screen of the universal portal link. Leave blank to hide it.</p>
        <div className="space-y-3">
          <Textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            maxLength={600}
            rows={4}
            placeholder="e.g. Welcome to Thermaco pricing. Questions? Contact your rep at sales@thermaco.com or (800) 555-0100."
            className="rounded-sm resize-none"
            data-testid="portal-message-input"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{msg.length}/600</span>
            <Button onClick={saveMessage} disabled={msgBusy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-portal-message-button">
              {msgBusy ? "Saving…" : "Save message"}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {user?.is_admin && (
      <Card className="rounded-sm p-6 mb-6" data-testid="alert-settings-card">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
          <BellRing className="h-4 w-4" /> Price-change alerts
        </h2>
        <p className="text-sm text-slate-500 mb-4">Email a summary to staff whenever a price import changes the catalog.</p>
        <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2.5 mb-4">
          <Label className="text-sm">Send alerts on price changes</Label>
          <Switch
            checked={alert.enabled}
            onCheckedChange={(v) => saveAlerts(v, alert.recipients)}
            disabled={alertBusy}
            data-testid="alert-enabled-switch"
          />
        </div>
        {alert.enabled && (
          <div>
            <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-2">Recipients</div>
            <p className="text-xs text-slate-400 mb-3">Leave all unchecked to notify every active staff member.</p>
            <div className="space-y-2 max-h-56 overflow-auto border border-slate-200 rounded-sm p-3">
              {alert.staff.length === 0 && <div className="text-sm text-slate-400">No staff accounts found.</div>}
              {alert.staff.map((s) => (
                <label key={s.email} className="flex items-center gap-3 text-sm cursor-pointer" data-testid={`alert-recipient-${s.email}`}>
                  <input
                    type="checkbox"
                    checked={alert.recipients.includes(s.email)}
                    onChange={() => toggleRecipient(s.email)}
                    className="h-4 w-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-500"
                  />
                  <span className="text-slate-900">{s.name || s.email}</span>
                  <span className="text-slate-400">{s.email}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-3 gap-2">
              <Button onClick={sendTestAlert} variant="outline" disabled={alertBusy} className="rounded-sm" data-testid="send-test-alert-button">
                Send me a test alert
              </Button>
              <Button onClick={() => saveAlerts(alert.enabled, alert.recipients)} disabled={alertBusy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-alert-recipients-button">
                {alertBusy ? "Saving…" : "Save recipients"}
              </Button>
            </div>
          </div>
        )}
      </Card>
      )}

      {user?.is_admin && <ShippingSettingsCard />}

      <Card className="rounded-sm p-6">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4" /> Change password
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required className="rounded-sm" data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required className="rounded-sm" data-testid="new-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="change-password-button">
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
